import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";

const SECTIONS = [
  {
    id: "training",
    title: "Training with The Aesthetics Academy Co.",
    items: [
      {
        q: "What types of training do you offer?",
        a: "We offer both accredited CPD training and regulated qualifications across aesthetics and education. Our accredited CPD courses are designed to develop knowledge and practical skills in specific treatments, while our regulated qualifications provide structured learning and assessment for those looking to gain or progress their recognised qualifications.",
      },
      {
        q: "What is the difference between an accredited CPD course and a regulated qualification?",
        a: "Accredited CPD courses are professional development courses designed to build knowledge and practical skills in specific treatments or areas of practice. Regulated qualifications follow an approved qualification specification and include structured learning and assessment leading to a nationally recognised qualification.",
      },
      {
        q: "Where does training take place?",
        a: "Our training is delivered in Sutton Coldfield within a professional learning environment designed for both theory and hands on practical training. Full location details and joining instructions will be provided before your training.",
      },
      {
        q: "Who delivers the training?",
        a: "Training is delivered by experienced Advanced Aesthetics Practitioners and Educators. Where assessment forms part of a regulated qualification, this is carried out by appropriately qualified assessors in accordance with the requirements of the qualification.",
      },
      {
        q: "How many students are in each class?",
        a: "We deliberately keep our class sizes small to ensure every learner receives individual guidance, practical experience and constructive feedback throughout their training.",
      },
    ],
  },
  {
    id: "choosing",
    title: "Choosing Your Course",
    items: [
      {
        q: "I'm new to aesthetics. Where should I start?",
        a: "The most appropriate starting point will depend on your existing qualifications, experience and career goals. If you're unsure which course or qualification is right for you, contact our team and we'll be happy to discuss your options and help you understand the most appropriate progression pathway.",
      },
      {
        q: "Do I need previous qualifications to enrol?",
        a: "Entry requirements vary depending on the course or qualification. Some courses are suitable for beginners, while others require previous qualifications or relevant industry experience. Any specific prerequisites will be shown on the individual course information and confirmed before enrolment.",
      },
      {
        q: "Can I take an advanced course without completing foundation training first?",
        a: "Where a course requires foundation knowledge, previous training or specific qualifications, these requirements must be met before attending. This helps us ensure training remains safe and appropriate for learners and models and that you have the underpinning knowledge required to progress to more advanced techniques.",
      },
      {
        q: "Do you offer training for experienced practitioners?",
        a: "Yes. We offer training for learners at different stages of their careers, from those entering aesthetics through to experienced practitioners looking to develop advanced treatment skills, progress to higher level qualifications or move into education and assessment.",
      },
      {
        q: "I'm not sure whether I need CPD training or a regulated qualification. Can you advise me?",
        a: "Absolutely. Contact us with details of your existing qualifications, experience and what you would like to achieve. Our team can talk you through the available options and help you understand which training pathway may be most appropriate for your professional development.",
      },
    ],
  },
  {
    id: "practical",
    title: "Practical Training and Models",
    items: [
      {
        q: "Is the training hands on?",
        a: "Yes. Practical experience is an important part of our training. Where appropriate to the course, learners will develop their skills through supervised hands on practical training alongside the required underpinning theory.",
      },
      {
        q: "Will I train on live models?",
        a: "Many of our practical courses include supervised training with live models, allowing learners to develop practical skills within a professional and supportive environment. The exact practical requirements vary depending on the course or qualification and will be explained before your training.",
      },
      {
        q: "Do I need to provide my own model?",
        a: "Where models are required, we will confirm whether they are provided by The Aesthetics Academy Co. or whether you will need to arrange a suitable model. This can vary depending on the individual course, treatment and assessment requirements.",
      },
      {
        q: "What happens if a model is unsuitable for treatment?",
        a: "Safety always comes first. All models must meet the relevant treatment criteria and complete the appropriate consultation and consent process. If a contraindication or other reason is identified that makes the treatment unsuitable, the treatment will not proceed. Where possible, our team will advise you on the next steps.",
      },
    ],
  },
  {
    id: "assessment",
    title: "Assessment and Certification",
    items: [
      {
        q: "Will I receive a certificate?",
        a: "Yes. The certificate you receive will depend on the type of training completed. Successful completion of an accredited CPD course leads to the relevant course certification. Regulated qualifications are subject to the required assessment, quality assurance and awarding processes before certification can be issued.",
      },
      {
        q: "Do I automatically pass after attending a course?",
        a: "No. Attendance alone does not guarantee successful completion where assessment is required. Learners must demonstrate the required knowledge, understanding and practical competence applicable to their individual course or qualification. Our aim is to support you throughout this process and provide constructive feedback where further development is required.",
      },
      {
        q: "Are there assessments?",
        a: "Yes, where required by the course or qualification. Assessment methods may include theory assessments, practical observations, written work, case studies, portfolios or other evidence of knowledge and competence. Full assessment requirements will be explained as part of your course information.",
      },
      {
        q: "Will I need to complete case studies?",
        a: "Some courses and regulated qualifications require case studies or additional evidence outside of the classroom. Where this applies, you will be given clear guidance on what is required, the evidence that needs to be submitted and the timeframe for completion.",
      },
      {
        q: "How long do I have to complete a regulated qualification?",
        a: "Completion times vary depending on the individual qualification, its assessment requirements and your own progress. Your expected completion period, assessment requirements and any applicable deadlines will be explained before enrolment.",
      },
    ],
  },
  {
    id: "payments",
    title: "Payments and Enrolment",
    items: [
      {
        q: "How do I secure my place?",
        a: "Booking and enrolment requirements vary depending on the course selected. You will be provided with the relevant booking, payment and enrolment information before your place is confirmed.",
      },
      {
        q: "Do regulated qualifications require an enrolment fee?",
        a: "Yes. Regulated qualifications require a non refundable enrolment fee before learner registration. This fee covers registration with the relevant awarding organisation and becomes non refundable once your enrolment has been processed. The remaining course balance can be paid in accordance with the payment options available for your chosen qualification.",
      },
      {
        q: "Can I pay in instalments?",
        a: "We offer a range of flexible payment options, subject to availability, eligibility and the terms applicable to the individual course and payment provider. Please visit our Payment Options page or contact our team to discuss the options currently available.",
      },
      {
        q: "Can I pay by bank transfer?",
        a: "Yes. Payment can be made securely by bank transfer. Please contact our team for payment details and include your name and course reference when making payment.",
      },
      {
        q: "Do you accept cash payments?",
        a: "Yes. Cash payment is available for learners who prefer to pay for their training in full. Please contact our team to arrange payment.",
      },
    ],
  },
  {
    id: "after",
    title: "After Your Training",
    items: [
      {
        q: "Do you provide support after the course?",
        a: "Yes. Ongoing support is an important part of our approach to education. All learners are invited to join our private student WhatsApp support group, providing a space to ask questions, seek guidance and stay connected with The Aesthetics Academy Co. and fellow learners after training. We want our learners to feel supported not only during their course, but as they continue to develop their knowledge, practical skills and confidence in practice.",
      },
      {
        q: "How long do I receive support for?",
        a: "Our learner support does not simply end when your training day is over. Learners can continue to access our student support community after completing their training, allowing you to ask questions and seek guidance as you continue developing your skills.",
      },
      {
        q: "Will I be able to get insurance after completing my course?",
        a: "Insurance requirements vary between providers and may depend on your existing qualifications, experience and the treatments you intend to offer. We recommend checking eligibility with your chosen insurance provider before enrolling if obtaining insurance for a particular treatment is essential to your intended scope of practice.",
      },
      {
        q: "Does completing a course mean I can immediately offer the treatment to clients?",
        a: "Completing training is one part of professional practice. Before offering any treatment independently, you are responsible for ensuring you have the appropriate training or qualifications, competence, insurance and any other requirements applicable to the treatment and your area of practice.",
      },
      {
        q: "Can I return for further training?",
        a: "Absolutely. Our training pathways are designed to allow learners to continue developing their skills, knowledge and qualifications as their careers progress. Whether you want to add a new treatment, develop advanced skills, progress to higher level qualifications or move into education and assessment, our team can help you explore your next step.",
      },
    ],
  },
  {
    id: "regulated",
    title: "Regulated Qualifications",
    items: [
      {
        q: "Who awards the regulated qualifications?",
        a: "The relevant awarding organisation and qualification details will be clearly identified within the information for each regulated qualification. Learners are registered and assessed in accordance with the requirements applicable to their chosen qualification.",
      },
      {
        q: "Are your regulated qualifications Ofqual regulated?",
        a: "Where a qualification is described as Ofqual regulated, the qualification itself is regulated by Ofqual and awarded by the relevant recognised awarding organisation. Full awarding organisation and qualification details will be provided on the relevant course page.",
      },
      {
        q: "Are regulated qualifications different from your accredited CPD courses?",
        a: "Yes. Our accredited CPD courses provide professional development and treatment specific training. Our regulated qualifications follow formal qualification specifications and require learners to complete the relevant learning, practical and assessment requirements before the qualification can be awarded. If you're unsure which option is right for you, our team can help you understand the difference and discuss the most appropriate pathway based on your existing qualifications and career goals.",
      },
    ],
  },
  {
    id: "before",
    title: "Before You Attend",
    items: [
      {
        q: "Is there a dress code for training?",
        a: "Yes. All learners are expected to attend practical training wearing clean antimicrobial scrubs. Long hair must be securely tied back, and closed toe footwear must be worn. Learners are expected to maintain a professional appearance and appropriate standards of personal hygiene throughout training. These requirements help us maintain high standards of infection prevention, safety and professionalism within the training environment.",
      },
      {
        q: "What should I bring to training?",
        a: "You will receive joining instructions before your course confirming anything you need to bring, along with your training times, location and any preparation required before attending.",
      },
      {
        q: "Do I need to bring my own equipment or products?",
        a: "This will depend on the course you are attending. Your course information and joining instructions will explain what is provided by the academy and whether you are required to bring any specific equipment, products or materials.",
      },
      {
        q: "What happens if I can't attend my training date?",
        a: "Cancellations, transfers and non attendance are subject to our Terms & Conditions. We recommend reading these carefully before booking a course or enrolling onto a regulated qualification. If you are unable to attend, please contact us as soon as possible so we can advise you of the options available under the applicable terms.",
      },
    ],
  },
];

const AccordionItem = ({ item, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        type="button"
        data-testid={`faq-item-${id}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left py-6 flex items-start justify-between gap-6 group"
        aria-expanded={open}
      >
        <span className="font-playfair text-lg md:text-xl text-white group-hover:text-gold transition-colors leading-snug">
          {item.q}
        </span>
        <ChevronDown
          size={22}
          strokeWidth={1.4}
          className={`shrink-0 mt-1 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-out ${
          open ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <p className="pb-7 pr-10 text-white/65 leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
};

export default function FAQ() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <section className="relative pt-40 md:pt-52 pb-16 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Overline className="mb-5 block">Frequently Asked Questions</Overline>
          <MaskedLines
            className="font-playfair uppercase leading-[0.95] tracking-tight"
            lineClassName="text-5xl md:text-7xl"
            lines={[<span key="1" className="text-white">Your Questions.</span>, <span key="2" className="text-gold">Answered.</span>]}
          />
          <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
            Everything you need to know about training, choosing a course, assessment, payments and the support we provide after your training is complete.
          </p>
        </div>
      </section>

      {/* Anchor nav */}
      <section className="py-6 bg-navy-surface border-y border-white/10 sticky top-[64px] z-30 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 overflow-x-auto">
          <ul className="flex gap-6 min-w-max" data-testid="faq-anchor-nav">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  data-testid={`faq-anchor-${s.id}`}
                  className="text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-gold transition-colors whitespace-nowrap"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-20">
          {SECTIONS.map((s, i) => (
            <div key={s.id} id={s.id} className="scroll-mt-40">
              <Reveal>
                <Overline>Section {String(i + 1).padStart(2, "0")}</Overline>
                <h2 className="font-playfair text-3xl md:text-4xl text-white mt-4 mb-10 leading-tight">{s.title}</h2>
              </Reveal>
              <div>
                {s.items.map((item, k) => (
                  <AccordionItem key={k} item={item} id={`${s.id}-${k}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-navy-surface border-t border-white/10 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <Overline>Still have a question?</Overline>
            <h3 className="font-playfair text-3xl md:text-5xl text-white mt-5 mb-6 leading-tight">
              Can&apos;t find the answer you&apos;re looking for?
            </h3>
            <p className="text-white/60 mb-10 leading-relaxed">
              Our team is here to help with questions about our accredited CPD training, regulated qualifications, entry requirements, payments and progression pathways.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <GoldButton onClick={() => openEnquiry()} data-testid="faq-enquire-button">Get In Touch</GoldButton>
              <Link to="/contact">
                <button className="px-8 py-4 text-[11px] tracking-[0.2em] uppercase border border-gold/50 text-gold hover:bg-gold/10 transition-colors" data-testid="faq-contact-link">
                  Visit Contact Page
                </button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
